import React from 'react'

// Hooks
import { useSearchFilters } from 'modules/shares/hooks/useSearchFilters'

// Components
import { DocumentHeader, Button, LinkButton } from '@sketch/components'
import { OpenInMacButton, useFlag, routes } from '@sketch/modules-common'
import { isMac } from '@sketch/utils'

// Local Components
import EmptyState from '../EmptyState'
import EmptyStateWithBanners from './EmptyStateWithBanners'
import RenewSubEmptyState from '../RenewSubEmptyState'
import DocumentListEmptyState from '../DocumentListEmptyState'

import {
  GetWorkspaceUploadsRestrictedForQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

type UploadsRestrictedForType = GetWorkspaceUploadsRestrictedForQuery['workspace']['uploadsRestrictedFor']

const EMPTY_STATE_COPY = {
  DEFAULT: {
    title: 'Your Workspace hasn’t created anything yet',
    description: 'Only Editors can create documents.',
    icon: 'document',
  },
  DEFAULT_EDITOR: {
    title: 'Your Workspace hasn’t created anything yet',
    description: 'Create and upload a document in Sketch to see it here.',
    icon: 'document',
  },
  TEMPLATE: {
    title: 'No templates added yet',
    description:
      "To change your document into a template, go to the document's settings and select Set As: Template. You can also add Sketch’s free templates here too. ",
    icon: 'template',
    editorialContent: 'empty-state-templates',
    actions: (
      <Button
        buttonStyle="primary-32"
        as="a"
        href="https://www.sketch.com/profile/sketch-templates/"
        rel="noopener noreferrer"
        target="_blank"
      >
        View Sketch Templates
      </Button>
    ),
  },
  SHARED_WITH_ME: {
    title: 'No documents shared with you yet',
    description:
      'When somebody else in the Workspace shares a Sketch document with you via email or mention, you’ll see it here.',
    icon: 'person-circle',
    editorialContent: 'empty-state-shared-with-me',
  },
  LIBRARIES: (workspaceId: string) =>
    ({
      title: 'No Libraries added yet',
      description:
        'To change your document into a Library, go to the document’s settings and select Set As: Library.',
      icon: 'link',
      editorialContent: 'empty-state-libraries',
      actions: (
        <LinkButton
          buttonStyle="primary-32"
          to={routes.WORKSPACE_SHARES.create({ workspaceId })}
        >
          View All Documents
        </LinkButton>
      ),
    } as const),
  LIBRARIES_EDITOR: (workspaceId: string) =>
    ({
      title: 'No Libraries added yet',
      description:
        'To change your document into a Library, go to the document’s settings and select Set As: Library.',
      icon: 'link',
      editorialContent: 'empty-state-libraries',
      actions: isMac() ? (
        <>
          <OpenInMacButton />
          <LinkButton
            buttonStyle="secondary-32"
            to={routes.WORKSPACE_SHARES.create({ workspaceId })}
          >
            View All Documents
          </LinkButton>
        </>
      ) : (
        <LinkButton
          buttonStyle="primary-32"
          to={routes.WORKSPACE_SHARES.create({ workspaceId })}
        >
          View All Documents
        </LinkButton>
      ),
    } as const),
  LIBRARIES_GUEST: {
    title: 'No Libraries',
    description: 'No Libraries added yet.',
    icon: 'link',
    editorialContent: 'empty-state-libraries',
  },
  NO_PROJECT: {
    title: 'Nothing to see here',
    description: 'There are no documents to show here.',
    icon: 'document',
  },
  ALL_DOCUMENTS_EDITOR: {
    title: 'No documents in the Workspace yet',
    description:
      'Create and save a document in the Mac app to see it here — or start by browsing our free templates.',
    icon: 'document',
    actions: (
      <>
        {isMac() ? <OpenInMacButton /> : null}
        <Button
          buttonStyle="secondary-32"
          as="a"
          href="https://www.sketch.com/profile/sketch-templates/"
          rel="noopener noreferrer"
          target="_blank"
        >
          View Sketch Templates
        </Button>
      </>
    ),
  },
} as const

type GetEmptyStateCopy<
  E extends keyof typeof EMPTY_STATE_COPY
> = typeof EMPTY_STATE_COPY[E]

/**
 * getEmptyStateCopy
 *
 * Validate that the empty state type is correct.
 * when the update to typescript 4.9 occurs we could use the "satisfies"
 */
const getEmptyStateCopy = <E extends keyof typeof EMPTY_STATE_COPY>(
  filter: E
): GetEmptyStateCopy<E> => EMPTY_STATE_COPY[filter]

interface ShareEmptyStateProps {
  filter: 'LIBRARY' | 'NO_PROJECT' | 'SHARED_WITH_ME' | 'TEMPLATE' | null
  search?: string
  workspace: WorkspaceMinimalFragment
  workspaceHasDocuments?: boolean | null
  uploadsRestrictedFor: UploadsRestrictedForType
}

export const ShareEmptyState: React.FC<ShareEmptyStateProps> = ({
  filter,
  search,
  workspace,
  workspaceHasDocuments,
  uploadsRestrictedFor,
}) => {
  const { userCanEdit, userRole, status } = workspace
  const { filters } = useSearchFilters()

  /**
   * This FF should be removed from here on the ticket
   * https://github.com/sketch-hq/Cloud/issues/16669
   */
  const isOnboardingEmptyStatesOn = useFlag('onboarding-empty-states')

  if (status === 'INACTIVE' && [null, 'NO_PROJECT'].includes(filter)) {
    return <RenewSubEmptyState workspace={workspace} />
  }

  /**
   * If the search prop contains a string value this view
   * should be always rendered independently of the FF "onboarding-empty-states"
   * value
   * https://sketch.slack.com/archives/C04N2EPHUGN/p1680696902971989?thread_ts=1680696398.697479&cid=C04N2EPHUGN
   */
  if (search || filters.length > 0) {
    return (
      <EmptyState
        icon="search"
        title="No search results"
        description="No document or Editor names matched your search."
        search={search}
      />
    )
  }

  /**
   * If the filter is "null" meaning the user is seeing the all document view,
   * the workspace has no documents and no upload restrictions are applied we
   * guess that this is a brand new workspace. Therefore we show to the user
   * some cards with actions so the user can start their journey
   */
  const showHelperCards =
    filter === null && !workspaceHasDocuments && uploadsRestrictedFor === 'NONE'

  if (showHelperCards && !isOnboardingEmptyStatesOn) {
    return (
      <>
        <DocumentHeader
          title="All Documents"
          description="All the documents in your Workspace — in sync and available anywhere."
        />
        <EmptyStateWithBanners workspace={workspace} />
      </>
    )
  }

  /**
   * If the FF "onboarding-empty-states" is on
   * we will render the new empty state with editorial content
   * otherwise default to the older style
   */
  const EmptyStateComponent = isOnboardingEmptyStatesOn
    ? DocumentListEmptyState
    : EmptyState

  if (filter === 'LIBRARY' && userRole === 'GUEST') {
    return <EmptyStateComponent {...getEmptyStateCopy('LIBRARIES_GUEST')} />
  }

  if (filter === 'LIBRARY' && userCanEdit) {
    return (
      <EmptyStateComponent
        {...getEmptyStateCopy('LIBRARIES_EDITOR')(workspace.identifier)}
      />
    )
  }

  if (filter === 'LIBRARY') {
    return (
      <EmptyStateComponent
        {...getEmptyStateCopy('LIBRARIES')(workspace.identifier)}
      />
    )
  }

  if (filter === 'SHARED_WITH_ME') {
    return <EmptyStateComponent {...getEmptyStateCopy('SHARED_WITH_ME')} />
  }

  if (filter === 'TEMPLATE') {
    return <EmptyStateComponent {...getEmptyStateCopy('TEMPLATE')} />
  }

  if (filter === 'NO_PROJECT') {
    return <EmptyStateComponent {...getEmptyStateCopy('NO_PROJECT')} />
  }

  if (filter === null && userCanEdit) {
    return (
      <EmptyStateComponent {...getEmptyStateCopy('ALL_DOCUMENTS_EDITOR')} />
    )
  }

  /**
   * If empty state is not handled by any of the filters above
   * then we resolve to a default one
   */

  const emptyStateProps = userCanEdit
    ? getEmptyStateCopy('DEFAULT_EDITOR')
    : getEmptyStateCopy('DEFAULT')

  return <EmptyStateComponent {...emptyStateProps} />
}

export default ShareEmptyState
